/**
 * Cuentas conectadas (IG/FB/WhatsApp) por cliente, con sus toggles.
 *
 * Endpoint devuelve cm_accounts joineado con cm_toggles, así un solo
 * fetch cubre listado + estado de cada interruptor por cuenta.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPatch, apiPost, api } from "@/lib/api";
import type { ApiAccount, ApiToggle } from "@/lib/adapters";
import type { ClientSlug, Platform } from "@/types";

export type KillSwitchLevel =
  | "none"
  | "bot_only"
  | "bot_and_escalations"
  | "full";

export interface AccountWithToggle extends ApiAccount {
  toggle: ApiToggle | null;
}

export function useAccounts(clientSlug?: ClientSlug) {
  return useQuery<AccountWithToggle[]>({
    queryKey: ["accounts", clientSlug ?? "all"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await apiGet<{ items: AccountWithToggle[] }>("/cm-accounts");
      if (!clientSlug) return res.items;
      return res.items.filter((a) => a.clientSlug === clientSlug);
    },
  });
}

interface TogglePayload {
  clientSlug: ClientSlug;
  platform: Platform;
  publishingEnabled?: boolean;
  moderationEnabled?: boolean;
  dryRun?: boolean;
}

export function useUpdateToggle() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; toggle: ApiToggle }, Error, TogglePayload>({
    mutationFn: async (payload) =>
      apiPatch<{ ok: boolean; toggle: ApiToggle }>(
        "/cm-accounts/toggle",
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("Cambio aplicado");
    },
    onError: (err) => {
      toast.error(`Error: ${err.message}`);
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

interface KillSwitchPayload {
  clientSlug: ClientSlug;
  platform: Platform;
  level: KillSwitchLevel;
  reason: string;
}

interface DeleteAccountPayload {
  accountId: number;
  reason: string;
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, DeleteAccountPayload>({
    mutationFn: async ({ accountId, reason }) =>
      api<{ ok: boolean }>(`/cm-accounts/${accountId}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("Cuenta desconectada");
    },
    onError: (err) => {
      toast.error(`No se pudo desconectar: ${err.message}`);
    },
  });
}

export interface AvailableAccount {
  platform: Platform;
  label: string;
  zernioProfileId: string;
  zernioAccountId: string;
  displayName: string | null;
}

/**
 * Plataformas conectadas en Zernio para el cliente que todavía NO están
 * cargadas como cuenta en el CM. Alimenta el "Agregar plataforma".
 */
export function useAvailableAccounts(clientSlug?: ClientSlug) {
  return useQuery<{ items: AvailableAccount[]; reason?: string }>({
    queryKey: ["accounts-available", clientSlug ?? "none"],
    enabled: !!clientSlug,
    staleTime: 30_000,
    queryFn: async () =>
      apiGet<{ items: AvailableAccount[]; reason?: string }>(
        `/cm-accounts/available?clientSlug=${encodeURIComponent(clientSlug!)}`,
      ),
  });
}

export function useAddAccount() {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean },
    Error,
    { clientSlug: ClientSlug; zernioAccountId: string; label: string }
  >({
    mutationFn: async ({ clientSlug, zernioAccountId }) =>
      apiPost<{ ok: boolean }>("/cm-accounts", { clientSlug, zernioAccountId }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["accounts-available"] });
      toast.success(`${vars.label} agregada`);
    },
    onError: (err) => {
      toast.error(`No se pudo agregar: ${err.message}`);
    },
  });
}

export function useSetKillSwitch() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, KillSwitchPayload>({
    mutationFn: async (payload) =>
      apiPost<{ ok: boolean }>("/cm-accounts/kill-switch", payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(
        vars.level === "none"
          ? "Freno desactivado"
          : `Freno aplicado: ${vars.level}`,
      );
    },
    onError: (err) => {
      toast.error(`Error: ${err.message}`);
    },
  });
}
